import React, { useState, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";

export default function FloatingWhatsApp({ settings, profileName = "Akhmad" }) {
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 1200);
        return () => clearTimeout(t);
    }, []);

    if (!settings?.whatsapp_floating_enabled || !settings?.whatsapp_number) return null;

    const number = String(settings.whatsapp_number).replace(/\D/g, "");
    const msg = settings.whatsapp_default_message || `Hello ${profileName}, I reviewed your portfolio and would like to discuss an engineering project.`;
    const link = `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;

    return (
        <div
            data-testid="floating-whatsapp"
            className={`fixed bottom-5 right-5 z-[60] transition-all duration-500 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        >
            {/* Popup card */}
            {open && (
                <div
                    data-testid="whatsapp-popup"
                    className="mb-3 w-[300px] rounded-2xl border border-white/10 bg-ink-900/95 backdrop-blur-xl shadow-2xl overflow-hidden animate-fade-up"
                >
                    <div className="px-5 pt-5 pb-4 border-b border-white/5 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                            <MessageCircle size={18} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-white font-medium text-sm">{profileName}</div>
                            <div className="flex items-center gap-1.5 text-xs text-emerald-300 font-mono">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400"></span>
                                </span>
                                Active now · replies in 24h
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="p-1.5 rounded-md hover:bg-white/10 text-ink-200"
                            aria-label="Close"
                        >
                            <X size={14} />
                        </button>
                    </div>
                    <div className="p-5">
                        <p className="text-sm text-ink-100 leading-relaxed">
                            👋 Hi! I typically respond within 24 hours. Send a direct WhatsApp message for the fastest reply.
                        </p>
                        <a
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="whatsapp-send-link"
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
                        >
                            <Send size={14} />
                            Start chat on WhatsApp
                        </a>
                        <p className="mt-3 text-[10px] text-ink-300 text-center font-mono">
                            Powered by WhatsApp · Tap to open
                        </p>
                    </div>
                </div>
            )}

            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="floating-whatsapp-button"
                aria-label="Chat on WhatsApp"
                className="group relative w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 shadow-[0_10px_40px_-10px_rgba(16,185,129,0.7)] flex items-center justify-center text-white transition-all hover:scale-110"
            >
                {!open && (
                    <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-50 animate-ping" />
                )}
                {open ? <X size={22} /> : <MessageCircle size={24} />}
            </button>
        </div>
    );
}
